import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Phone, ArrowRight, MapPin, Clock, CheckCircle2, Star, HeartHandshake, Stethoscope, Home as HomeIcon, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { cities, cityFaqTemplates, getCityKeywords } from '../data/citiesData';
import citySpecificFaqs from '../data/citySpecificFaqs';

export default function StadtSeiteV2() {
  const { stadt } = useParams();
  const city = cities.find(c => c.slug === stadt);

  if (!city) {
    return (
      <main className="pt-32 pb-20">
        <div className="container-custom text-center">
          <h1 className="text-3xl font-bold text-[#1e293b] mb-4">Stadt nicht gefunden</h1>
          <Link to="/" className="text-[#1A52D8] hover:underline">Zurück zur Startseite</Link>
        </div>
      </main>
    );
  }

  const faqs = citySpecificFaqs[city.slug] || cityFaqTemplates(city);
  const keywords = getCityKeywords(city);
  const currentYear = new Date().getFullYear();

  return (
    <>
      <Helmet>
        <title>Ambulante Pflege {city.name} | Phönix Pflegeteam | 24/7 Pflegedienst</title>
        <meta name="description" content={`Ambulante Pflege in ${city.name} ✓ 24/7 erreichbar ✓ Nur ${city.distance} km Anfahrt ✓ Grundpflege ✓ Behandlungspflege ✓ Pflegekasse übernimmt. Jetzt beraten lassen: 0151 46173685`} />
        <meta name="keywords" content={keywords.join(', ')} />
        <link rel="canonical" href={`https://pflegeteam-itzehoe.de/ambulante-pflege/${city.slug}`} />
        
        {/* Schema.org für diese Stadt */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "MedicalBusiness",
            "name": `Phönix Pflegeteam Itzehoe - Ambulante Pflege ${city.name}`,
            "description": `Ambulanter Pflegedienst in ${city.name}, ${city.distance} km von unserem Standort. ${city.specific}`,
            "areaServed": {
              "@type": "City",
              "name": city.name,
              "postalCode": city.postal
            },
            "provider": {
              "@id": "https://pflegeteam-itzehoe.de/#organization"
            }
          })}
        </script>
        
        {/* FAQ Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map(faq => ({
              "@type": "Question",
              "name": faq.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.answer
              }
            }))
          })}
        </script>

        {/* Breadcrumb Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://pflegeteam-itzehoe.de/"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": `Ambulante Pflege ${city.name}`,
                "item": `https://pflegeteam-itzehoe.de/ambulante-pflege/${city.slug}`
              }
            ]
          })}
        </script>
      </Helmet>

      <main id="main-content">
        {/* Hero Section - GEO Optimiert */}
        <section className="relative pt-32 pb-16 md:pt-40 md:pb-24 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-[#f8fafb] via-white to-[#e8f8f8]" />
          <div className="absolute top-20 right-0 w-[400px] h-[400px] bg-[#23D3D3]/5 rounded-full blur-3xl" />
          
          <div className="container-custom relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              {/* Breadcrumb */}
              <nav className="flex items-center justify-center gap-2 text-sm text-[#64748b] mb-6">
                <Link to="/" className="hover:text-[#1A52D8]">Home</Link>
                <span>/</span>
                <span className="text-[#1e293b]">Ambulante Pflege {city.name}</span>
              </nav>

              {/* Direct Answer für AI/Voice Search */}
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#23D3D3]/10 rounded-full mb-6">
                <MapPin className="w-4 h-4 text-[#23D3D3]" />
                <span className="text-sm font-medium text-[#23D3D3]">Nur {city.distance} km Anfahrt von Dägeling</span>
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#1e293b] leading-tight mb-6">
                Ambulante Pflege in <span className="text-[#1A52D8]">{city.name}</span>
              </h1>

              {/* Direct Answer Box - AI Citation Magnet */}
              <div className="bg-white rounded-2xl p-6 border-2 border-[#1A52D8]/20 mb-8 text-left direct-answer">
                <p className="text-lg text-[#475569] leading-relaxed">
                  <strong className="text-[#1A52D8]">Schnelle Antwort:</strong> Wir bieten professionelle ambulante Pflege in {city.name} mit Start innerhalb von 24 Stunden. 
                  Kosten: 45-55€/Stunde (meist vollständig von Pflegekasse übernommen). 
                  Anfahrt von unserem Standort: nur {city.distance} km. 
                  Verfügbar: 24/7 unter 0151 46173685.
                </p>
              </div>

              <p className="text-xl text-[#64748b] mb-8 leading-relaxed">
                {city.specific}
              </p>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                <a href="tel:+4915146173685">
                  <Button className="bg-[#1A52D8] text-white hover:bg-[#1545b8] h-14 px-8 rounded-full text-lg font-semibold shadow-lg">
                    <Phone className="w-5 h-5" />
                    Jetzt Anrufen
                  </Button>
                </a>
                <Link to="/kontakt">
                  <Button className="bg-white text-[#1A52D8] border-2 border-[#1A52D8]/20 hover:border-[#1A52D8] h-14 px-8 rounded-full text-lg font-semibold">
                    Rückruf anfordern
                    <ArrowRight className="w-5 h-5" />
                  </Button>
                </Link>
              </div>

              {/* Trust Badges */}
              <div className="flex flex-wrap justify-center gap-6 text-sm text-[#64748b]">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-[#23D3D3]" />
                  <span>Kostenlose Beratung</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-[#23D3D3]" />
                  <span>Start binnen 24h</span>
                </div>
                <div className="flex items-center gap-2">
                  <Star className="w-5 h-5 fill-[#fbbf24] text-[#fbbf24]" />
                  <span>4.9★ Bewertung</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Leistungen in dieser Stadt */}
        <section className="py-16 bg-white">
          <div className="container-custom">
            <h2 className="text-3xl font-bold text-[#1e293b] text-center mb-12">
              Unsere Leistungen in {city.name}
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {[
                { icon: HeartHandshake, name: 'Grundpflege', price: '45€/h' },
                { icon: Stethoscope, name: 'Behandlungspflege', price: '55€/h' },
                { icon: HomeIcon, name: 'Hauswirtschaft', price: '35€/h' }
              ].map((service, i) => (
                <div key={i} className="bg-gradient-to-br from-[#f8fafb] to-white rounded-xl p-6 border border-[#e2e8f0]">
                  <service.icon className="w-10 h-10 text-[#1A52D8] mb-3" />
                  <h3 className="font-bold text-[#1e293b] mb-2">{service.name}</h3>
                  <p className="text-[#23D3D3] font-semibold">{service.price}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Lokale Besonderheiten */}
        <section className="py-16 bg-[#f8fafb]">
          <div className="container-custom max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-[#1e293b] mb-6">
              Warum wir der richtige Pflegedienst für {city.name} sind
            </h2>
            <div className="bg-white rounded-2xl p-8 border border-[#e2e8f0]">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-bold text-[#1A52D8] mb-3 flex items-center gap-2">
                    <MapPin className="w-5 h-5" />
                    Lokal & Nah
                  </h3>
                  <p className="text-[#64748b]">
                    Von Dägeling nach {city.name}: nur {city.distance} km. 
                    Wir sind in unter {city.distance < 10 ? '15' : city.distance < 15 ? '20' : '30'} Minuten bei Ihnen.
                  </p>
                </div>
                <div>
                  <h3 className="font-bold text-[#1A52D8] mb-3 flex items-center gap-2">
                    <Shield className="w-5 h-5" />
                    Zugelassener Dienst
                  </h3>
                  <p className="text-[#64748b]">
                    Vollständig zugelassen, alle Pflegekassen akzeptiert. 
                    Abrechnung direkt mit Ihrer Kasse.
                  </p>
                </div>
                <div>
                  <h3 className="font-bold text-[#1A52D8] mb-3 flex items-center gap-2">
                    <Clock className="w-5 h-5" />
                    24/7 Verfügbar
                  </h3>
                  <p className="text-[#64748b]">
                    Auch nachts, am Wochenende und an Feiertagen für {city.name} erreichbar.
                  </p>
                </div>
                <div>
                  <h3 className="font-bold text-[#1A52D8] mb-3 flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5" />
                    Transparent
                  </h3>
                  <p className="text-[#64748b]">
                    Klare Preise, keine versteckten Kosten. 
                    Pflegekasse übernimmt meist alles.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ für diese Stadt */}
        <section className="py-16 bg-white">
          <div className="container-custom max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-[#1e293b] text-center mb-12">
              Häufige Fragen zur Pflege in {city.name}
            </h2>
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`faq-${index}`}
                  className="bg-[#f8fafb] rounded-xl px-6 border border-[#e2e8f0]"
                >
                  <AccordionTrigger className="text-left text-base font-semibold text-[#1e293b] hover:no-underline py-5">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-[#64748b] leading-relaxed pb-5">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* SEO Content - Stadt-spezifisch */}
        <section className="py-16 bg-[#f8fafb]">
          <div className="container-custom max-w-4xl mx-auto">
            <div className="prose prose-lg max-w-none text-[#475569]">
              <h2 className="text-2xl font-bold text-[#1e293b] mb-6">
                Ambulante Pflege in {city.name} - Ihr lokaler Partner
              </h2>
              
              <p className="mb-4">
                Sie suchen einen zuverlässigen <strong>Pflegedienst in {city.name}</strong>? 
                Das Phönix Pflegeteam Itzehoe ist Ihr Partner für professionelle <strong>häusliche Pflege in {city.name}</strong> und im gesamten Kreis Steinburg.
              </p>

              <p className="mb-4">
                {city.demographics}
              </p>

              <h3 className="text-xl font-bold text-[#1A52D8] mb-4 mt-8">
                Was macht uns in {city.name} besonders?
              </h3>
              
              <p className="mb-4">
                <strong>Kurze Anfahrtswege:</strong> Mit nur {city.distance} km Entfernung von unserem Standort in Dägeling 
                sind wir einer der nächstgelegenen Pflegedienste für {city.name}. Das bedeutet für Sie: 
                Flexible Termine, schnelle Reaktion bei Notfällen und persönliche Betreuung.
              </p>

              <p className="mb-4">
                <strong>Lokale Expertise:</strong> {city.specific} Wir kennen die Besonderheiten 
                von {city.name} und arbeiten eng mit örtlichen Ärzten, Apotheken und Therapeuten zusammen.
              </p>

              <h3 className="text-xl font-bold text-[#1A52D8] mb-4 mt-8">
                Typische Pflegesituationen in {city.name}
              </h3>
              
              <p className="mb-4">
                In {city.name} betreuen wir Menschen in verschiedenen Lebenssituationen:
              </p>

              <ul className="list-disc pl-6 mb-6 space-y-2">
                <li>Senioren nach Krankenhausaufenthalt (z.B. nach Schlaganfall, Hüft-OP)</li>
                <li>Menschen mit Demenz, die zuhause bleiben möchten</li>
                <li>Palliativpatienten in ihrer vertrauten Umgebung</li>
                <li>Entlastung für pflegende Angehörige (Verhinderungspflege)</li>
                <li>Behandlungspflege auf ärztliche Verordnung (Medikamente, Wundversorgung)</li>
              </ul>

              <h3 className="text-xl font-bold text-[#1A52D8] mb-4 mt-8">
                Kosten für ambulante Pflege in {city.name}
              </h3>
              
              <p className="mb-4">
                Die gute Nachricht: <strong>In den meisten Fällen übernimmt die Pflegekasse die Kosten vollständig.</strong>
              </p>

              <table className="w-full border-collapse mb-6">
                <thead>
                  <tr className="bg-[#f8fafb]">
                    <th className="border border-[#e2e8f0] p-3 text-left">Pflegegrad</th>
                    <th className="border border-[#e2e8f0] p-3 text-left">Monatliche Sachleistung</th>
                    <th className="border border-[#e2e8f0] p-3 text-left">Für Sie in {city.name}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-[#e2e8f0] p-3">Pflegegrad 2</td>
                    <td className="border border-[#e2e8f0] p-3">761€</td>
                    <td className="border border-[#e2e8f0] p-3">Ca. 17 Stunden Pflege/Monat</td>
                  </tr>
                  <tr>
                    <td className="border border-[#e2e8f0] p-3">Pflegegrad 3</td>
                    <td className="border border-[#e2e8f0] p-3">1.432€</td>
                    <td className="border border-[#e2e8f0] p-3">Ca. 32 Stunden Pflege/Monat</td>
                  </tr>
                  <tr>
                    <td className="border border-[#e2e8f0] p-3">Pflegegrad 4</td>
                    <td className="border border-[#e2e8f0] p-3">1.778€</td>
                    <td className="border border-[#e2e8f0] p-3">Ca. 39 Stunden Pflege/Monat</td>
                  </tr>
                  <tr>
                    <td className="border border-[#e2e8f0] p-3">Pflegegrad 5</td>
                    <td className="border border-[#e2e8f0] p-3">2.200€</td>
                    <td className="border border-[#e2e8f0] p-3">Ca. 49 Stunden Pflege/Monat</td>
                  </tr>
                </tbody>
              </table>

              <p className="mb-6">
                <strong>Wichtig:</strong> Diese Zahlen gelten für {city.name} wie überall in Deutschland. 
                Wir beraten Sie kostenlos, welcher Pflegegrad bei Ihnen realistisch ist und übernehmen 
                die gesamte Kommunikation mit der Pflegekasse.
              </p>

              <h3 className="text-xl font-bold text-[#1A52D8] mb-4 mt-8">
                Weitere Orte in der Nähe von {city.name}
              </h3>
              
              <p className="mb-4">
                Wir betreuen auch in allen Nachbargemeinden von {city.name}:
              </p>

              <div className="flex flex-wrap gap-3 mb-6">
                {cities.filter(c => c.slug !== city.slug).slice(0, 8).map(c => (
                  <Link 
                    key={c.slug}
                    to={`/ambulante-pflege/${c.slug}`}
                    className="px-4 py-2 bg-white rounded-full text-sm text-[#1A52D8] border border-[#e2e8f0] hover:border-[#1A52D8] hover:bg-[#1A52D8]/5 transition-all"
                  >
                    {c.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-br from-[#1A52D8] to-[#23D3D3] text-white">
          <div className="container-custom text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Brauchen Sie Pflege in {city.name}?
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Kostenlose Beratung, unverbindliches Erstgespräch bei Ihnen zuhause. 
              Wir sind in unter {city.distance < 10 ? '15' : city.distance < 15 ? '20' : '30'} Minuten bei Ihnen.
            </p>
            <a href="tel:+4915146173685">
              <Button className="bg-white text-[#1A52D8] hover:bg-white/90 h-14 px-10 rounded-full text-lg font-semibold shadow-xl">
                <Phone className="w-5 h-5" />
                Jetzt Anrufen: 0151 46173685
              </Button>
            </a>
          </div>
        </section>
      </main>
    </>
  );
}
